import React, {useState, useEffect, useRef} from 'react';
import post from '../utils/post.js';
import AppHeader from '../components/AppHeader.js';

import notificationSound from '../audio1.wav';
import notificationSound2 from '../audio2.wav';

const audio1 = new Audio(notificationSound);
const audio2 = new Audio(notificationSound2);

const timerValueAdjustments = [-10, -1, 1, 10];

const getAdjustmentLabel = (value) => {
	return value > 0 ? `+${value}` : `${value}`;
};

export default function Exercise({usrInfo, timerCnt}) {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [started, setStarted] = useState(false);
	const [counter, setCounter] = useState({actTime: '00:00', actMsg: '', actColor: 'fnt_orange', actRound: 0});
	const timerStartedAt = useRef(0);
	const timerIntervalId = useRef(null);
	const timerConfig = useRef({prep: 0, work: 0, rest: 0, rounds: 1, sound: 0});
	const lastTick = useRef(null);

	useEffect(() => {
		getData();
	}, []);

	const getData = () => {
		setLoading(true);

		post('getTimerNew', {})
			.then((res) => {
				setData(res.data);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	function convertSeconds(s) {
		let min = Math.floor(s / 60);
		let sec = s % 60;
		min = min.toString().length === 1 ? '0' + min.toString() : min.toString();
		sec = sec.toString().length === 1 ? '0' + sec.toString() : sec.toString();
		return min + ':' + sec;
	}

	function setTime1Val(val) {
		data.TIMER_TIME1 += val;
		if (data.TIMER_TIME1 < 10) {
			data.TIMER_TIME1 = 10;
		}
		setData({...data});
	}

	function setTime2Val(val) {
		data.TIMER_TIME2 += val;
		if (data.TIMER_TIME2 < 0) {
			data.TIMER_TIME2 = 0;
		}
		setData({...data});
	}

	function setTime3Val(val) {
		data.TIMER_TIME3 += val;
		if (data.TIMER_TIME3 < 0) {
			data.TIMER_TIME3 = 0;
		}
		setData({...data});
	}

	function setRoundVal(val) {
		data.TIMER_ROUND += val;
		if (data.TIMER_ROUND < 1) {
			data.TIMER_ROUND = 1;
		}
		setData({...data});
	}

	// Stav časovače se odvozuje z uplynulého času, neodečítá se po sekundách. Když
	// mobil uspí aplikaci a timery zamrznou, po probuzení navazuje časovač tam, kde
	// by reálně byl, místo aby se opozdil o celou dobu spánku.
	function buildTimerState(elapsedSec) {
		const {prep, work, rest, rounds} = timerConfig.current;

		if (elapsedSec < prep) {
			return {phase: 'prep', round: 1, remaining: prep - elapsedSec, msg: 'Připrav se !!!', color: 'fnt_orange', done: false};
		}

		let offset = elapsedSec - prep;

		for (let round = 1; round <= rounds; round++) {
			if (offset < work) {
				return {phase: 'work', round: round, remaining: work - offset, msg: 'Makej !!!', color: 'fnt_red', done: false};
			}

			offset -= work;

			if (round < rounds) {
				if (offset < rest) {
					return {phase: 'rest', round: round, remaining: rest - offset, msg: 'Odpočívej !!!', color: 'fnt_green', done: false};
				}

				offset -= rest;
			}
		}

		return {phase: 'done', round: rounds, remaining: 0, msg: 'Hotovo !!!', color: 'fnt_green', done: true};
	}

	useEffect(() => {
		if (!started) {
			if (timerIntervalId.current) {
				clearInterval(timerIntervalId.current);
				timerIntervalId.current = null;
			}

			return undefined;
		}

		const syncTimer = () => {
			const now = Date.now();
			const elapsedSec = Math.max(0, Math.floor((now - timerStartedAt.current) / 1000));
			const state = buildTimerState(elapsedSec);
			const previous = lastTick.current;
			// Po probuzení z uspání zvuky přeskočíme, ať se nepřehraje všechno, co mezitím propadlo.
			const isContinuous = previous !== null && now - previous.at <= 2000;

			if (timerConfig.current.sound === 1 && isContinuous) {
				if (state.phase !== previous.phase || state.round !== previous.round) {
					audio2.play().catch(() => {});
				} else if (!state.done && state.remaining <= 3 && state.remaining !== previous.remaining) {
					audio1.play().catch(() => {});
				}
			}

			lastTick.current = {phase: state.phase, round: state.round, remaining: state.remaining, at: now};
			setCounter({actTime: convertSeconds(state.remaining), actMsg: state.msg, actColor: state.color, actRound: state.round});

			if (state.done && timerIntervalId.current) {
				clearInterval(timerIntervalId.current);
				timerIntervalId.current = null;
			}
		};

		syncTimer();
		timerIntervalId.current = setInterval(syncTimer, 250);
		document.addEventListener('visibilitychange', syncTimer);
		window.addEventListener('focus', syncTimer);
		window.addEventListener('pageshow', syncTimer);

		return () => {
			if (timerIntervalId.current) {
				clearInterval(timerIntervalId.current);
				timerIntervalId.current = null;
			}

			document.removeEventListener('visibilitychange', syncTimer);
			window.removeEventListener('focus', syncTimer);
			window.removeEventListener('pageshow', syncTimer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [started]);

	const start = () => {
		timerConfig.current = {
			prep: parseInt(data.TIMER_TIME3, 10) || 0,
			work: parseInt(data.TIMER_TIME1, 10) || 0,
			rest: parseInt(data.TIMER_TIME2, 10) || 0,
			rounds: parseInt(data.TIMER_ROUND, 10) || 1,
			sound: parseInt(data.TIMER_SOUND, 10) === 1 ? 1 : 0,
		};

		timerStartedAt.current = Date.now();
		lastTick.current = null;

		const initialState = buildTimerState(0);
		setCounter({actTime: convertSeconds(initialState.remaining), actMsg: initialState.msg, actColor: initialState.color, actRound: initialState.round});

		setStarted(true);

		setLoading(true);

		post('saveTimerStart', {
			ROUND: data.TIMER_ROUND,
			TIME1: data.TIMER_TIME1,
			TIME2: data.TIMER_TIME2,
			TIME3: data.TIMER_TIME3,
			SOUND: data.TIMER_SOUND,
		})
			.then((res) => {
				setLoading(false);
			})
			.catch((error) => {
				setLoading(false);
				console.error(error);
			});
	};

	function resize(el, factor) {
		if (!el) return;

		const surface = el.closest('.timer-page__active-surface');
		const head = surface?.querySelector('.timer-page__active-head');
		const headHeight = head?.offsetHeight || 0;
		const surfaceStyles = surface ? window.getComputedStyle(surface) : null;
		const verticalPadding = surfaceStyles ? parseFloat(surfaceStyles.paddingTop || 0) + parseFloat(surfaceStyles.paddingBottom || 0) : 0;
		const availableWidth = el.clientWidth || el.offsetWidth;
		const availableHeight = Math.max((surface?.clientHeight || window.innerHeight) - headHeight - verticalPadding, 0);

		if (typeof factor === 'undefined') {
			factor = 5;
		}

		const widthSize = (availableWidth / factor) | 0;
		const heightSize = (availableHeight * 0.72) | 0;
		el.style.fontSize = `${Math.max(Math.min(widthSize, heightSize), 56)}px`;
	}

	useEffect(() => {
		resize(document.getElementById('ACT_TIMER'), 3);
	}, [counter.actTime]);

	useEffect(() => {
		if (!started) {
			return undefined;
		}

		const handleTimerResize = () => {
			window.requestAnimationFrame(() => resize(document.getElementById('ACT_TIMER'), 3));
		};

		handleTimerResize();
		window.addEventListener('resize', handleTimerResize);
		window.addEventListener('orientationchange', handleTimerResize);

		return () => {
			window.removeEventListener('resize', handleTimerResize);
			window.removeEventListener('orientationchange', handleTimerResize);
		};
	}, [started]);

	const stop = () => {
		timerStartedAt.current = 0;
		lastTick.current = null;
		setStarted(false);
	};

	const setSoundVal = (e) => {
		e.preventDefault();
		data.TIMER_SOUND = parseInt(data.TIMER_SOUND) === 1 ? 0 : 1;
		setData({...data});
	};

	let time1 = convertSeconds(data.TIMER_TIME1);
	let time2 = convertSeconds(data.TIMER_TIME2);
	let time3 = convertSeconds(data.TIMER_TIME3);
	let round = data.TIMER_ROUND;
	let timeTotal = convertSeconds(data.TIMER_TIME1 * data.TIMER_ROUND + data.TIMER_TIME2 * (data.TIMER_ROUND - 1));

	const setupCards = [
		{
			label: 'Čas kola',
			value: time1,
			adjustments: timerValueAdjustments,
			onAdjust: setTime1Val,
		},
		{
			label: 'Čas odpočinku',
			value: time2,
			adjustments: timerValueAdjustments,
			onAdjust: setTime2Val,
		},
		{
			label: 'Počet kol',
			value: round,
			adjustments: [-1, 1],
			onAdjust: setRoundVal,
		},
		{
			label: 'Odložit start o',
			value: time3,
			adjustments: timerValueAdjustments,
			onAdjust: setTime3Val,
		},
	];

	return (
		<>
			<div className="page_main">
				<AppHeader usrInfo={usrInfo} timerCnt={timerCnt} loading={loading} />

				<div className={`page timer-page${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''}`}>
					{!started ? (
						<div className="timer-page__shell">
							<section className="timer-page__hero">
								<div className="timer-page__hero-main">
									<h1 className="timer-page__title">Intervalový časovač</h1>
								</div>
								<div className="timer-page__summary-grid">
									<div className="timer-page__summary-card">
										<div className="timer-page__summary-label">Kolo</div>
										<div className="timer-page__summary-value">{time1}</div>
									</div>
									<div className="timer-page__summary-card">
										<div className="timer-page__summary-label">Celkem</div>
										<div className="timer-page__summary-value">{timeTotal}</div>
									</div>
								</div>
							</section>

							<div className="timer-page__setup-grid">
								{setupCards.map((card) => (
									<section className="timer-page__card" key={card.label}>
										<div className="timer-page__card-label">{card.label}</div>
										<div className="timer-page__card-value">{card.value}</div>
										<div className={`timer-page__adjustments${card.adjustments.length === 4 ? ' timer-page__adjustments--four' : ''}`}>
											{card.adjustments.map((adjustment) => (
												<button key={`${card.label}_${adjustment}`} type="button" className="timer-page__adjust-button" onClick={() => card.onAdjust(adjustment)}>
													{getAdjustmentLabel(adjustment)}
												</button>
											))}
										</div>
									</section>
								))}

								<section className="timer-page__card timer-page__card--summary">
									<div className="timer-page__card-label">Délka tréninku</div>
									<div className="timer-page__card-value">{timeTotal}</div>
									<button type="button" className={`timer-page__sound-toggle${parseInt(data.TIMER_SOUND) === 1 ? ' is-active' : ''}`} onClick={(e) => setSoundVal(e)} aria-pressed={parseInt(data.TIMER_SOUND) === 1}>
										<span className="timer-page__sound-check" aria-hidden="true"></span>
										<span className="timer-page__sound-label">Zvuky</span>
									</button>
								</section>
							</div>

							<div className="timer-page__actions">
								<button type="button" className="timer-page__start-button" onClick={() => start()}>
									Start
								</button>
							</div>
						</div>
					) : (
						<section className="timer-page__active-surface">
							<div className="timer-page__active-head">
								<button type="button" className="timer-page__back-button" onClick={() => stop()}>
									Zpět
								</button>
								<div className={`timer-page__active-message ${counter.actColor}`}>{counter.actMsg}</div>
								<div className="timer-page__active-round">{counter.actRound}/{round}</div>
							</div>

							<div className="timer-page__active-timer" id="ACT_TIMER">
								<b>{counter.actTime}</b>
							</div>
						</section>
					)}
				</div>
			</div>
		</>
	);
}
